import { 
    AGE_LIMITS,
    BENEFIT_VALIDATION_KEYS,
    DEPENDENT_RELATIONSHIPS,
    VALIDATION_STEPS 
} from "constants/validationConstants";
import { useCotizador } from "context/apiContext";
import { TextsContext } from "context/textsContext";
import { getAge, normalizeStepValue, setFieldValidationError } from "helpers/toolsHelper";
import { useSteps } from "hooks/useSteps";
import { useContext } from "react";

const useFormValidation = () => {
    const { texts } = useContext(TextsContext);
    const { errors, contextState, selectedBenefitsIds } = useCotizador();
    const { getCurrentStepData } = useSteps();

    const validateBirthdayAge = ( birthday, coverage, component = null) => {
        
        if (!birthday) return null;
        
        const personAge = getAge(birthday, coverage)
        const isSpouse = component == DEPENDENT_RELATIONSHIPS.SPOUSE

        if (personAge < AGE_LIMITS.MIN_AGE) {
            return isSpouse 
                ? texts?.general_field_validation_spouse_age 
                : texts?.general_field_validation_age;
        }

        if (personAge > AGE_LIMITS.MAX_AGE) {
            return isSpouse 
                ? texts?.general_field_validation_spouse_age_more_than_75 
                : texts?.general_field_validation_age_greater_than_75;
        }

        return null;
    }

    const validateOrganRiderSelection = (validationErrors, shouldCleanExisting = false) => {
        const hasOrganButNoRider = contextState.organ && contextState.organRider === 0;
        const requiredMessage = texts?.general_field_validation_required ?? 'This field is required';
        
        setFieldValidationError(
            validationErrors,
            'organ',
            errors,
            hasOrganButNoRider,
            requiredMessage,
            shouldCleanExisting
        )
    }

    const validateAcceptanceCheckboxes = (validationErrors, shouldCleanExisting = false) => {
        const requiredMessage = texts?.general_field_validation_required ?? 'This field is required';
        
        setFieldValidationError(
            validationErrors,
            'acceptNonRegion',
            errors,
            !contextState.acceptNonRegion,
            requiredMessage,
            shouldCleanExisting
        );

        setFieldValidationError(
            validationErrors,
            'acceptNonRegion2',
            errors,
            !contextState.acceptNonRegion2,
            requiredMessage,
            shouldCleanExisting
        )
    }

    const validateLimitedCoverageAcceptance = (validationErrors, shouldCleanExisting = false) => {
        const requiredMessage = texts?.general_field_validation_required ?? 'This field is required';
        
        setFieldValidationError(
            validationErrors,
            'limitedCover',
            errors,
            !contextState.limitedCover,
            requiredMessage,
            shouldCleanExisting
        )
    }

    const validateAmbulanceAcceptance = (validationErrors, shouldCleanExisting = false) => {
        const requiredMessage = texts?.general_field_validation_required ?? 'This field is required';
        
        setFieldValidationError(
            validationErrors,
            'nonAmbulance',
            errors,
            !contextState.nonAmbulance,
            requiredMessage,
            shouldCleanExisting
        )
    }

    const validateMaternitySelection = (validationErrors, shouldCleanExisting = false) => {
        const maternityBenefitValue = selectedBenefitsIds[BENEFIT_VALIDATION_KEYS.MATERNITY_AND_CESAREAN];
        const newbornBenefitValue = selectedBenefitsIds[BENEFIT_VALIDATION_KEYS.MATERNITY];
        
        const isMaternityNo = maternityBenefitValue === BENEFIT_VALIDATION_KEYS.MATERNITY_ID_182;
        const isNewbornNo = newbornBenefitValue === BENEFIT_VALIDATION_KEYS.MATERNITY_NO_VALUE;
        
        const needsMaternityConfirmation = !contextState.nonMaternidad || 
            (!contextState.nonMaternidad && (isMaternityNo || isNewbornNo));
        
        const requiredMessage = texts?.general_field_validation_required ?? 'This field is required';
        
        setFieldValidationError(
            validationErrors,
            'nonMaternidad',
            errors,
            needsMaternityConfirmation,
            requiredMessage,
            shouldCleanExisting
        )
    }

    const validateDependantsTable = (validationErrors, shouldCleanExisting = false) => {
        const hasNoDependantsButAddSelected = contextState?.dependants.length === 0 && 
            contextState?.addDependant;
        
        const requiredMessage = texts?.general_field_validation_required ?? 'This field is required';
        
        setFieldValidationError(
            validationErrors,
            'dependantsTable',
            errors,
            hasNoDependantsButAddSelected,
            requiredMessage,
            shouldCleanExisting
        )
    }

    const validateStepSpecificRules = (shouldCleanExisting = false) => {
        const stepValidationErrors = {};
        const currentStep = normalizeStepValue(getCurrentStepData?.()?.step);

        if (currentStep >= VALIDATION_STEPS.STEP_5) {
            validateOrganRiderSelection(stepValidationErrors, shouldCleanExisting);
        }

        if (currentStep === VALIDATION_STEPS.STEP_9) {
            validateAcceptanceCheckboxes(stepValidationErrors, shouldCleanExisting);
        }

        if (currentStep === VALIDATION_STEPS.STEP_4) {
            validateLimitedCoverageAcceptance(stepValidationErrors, shouldCleanExisting);
        }

        if (currentStep === VALIDATION_STEPS.STEP_6) {
            validateAmbulanceAcceptance(stepValidationErrors, shouldCleanExisting);
        }

        if (currentStep === VALIDATION_STEPS.STEP_8) {
            validateMaternitySelection(stepValidationErrors, shouldCleanExisting);
        }

        if (currentStep === VALIDATION_STEPS.STEP_2) {
            validateDependantsTable(stepValidationErrors, shouldCleanExisting);
        }

        return stepValidationErrors;
    }

    return { 
        validateBirthdayAge,
        validateStepSpecificRules, 
    }
}

export default useFormValidation;